import { useContext, useEffect, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Skeleton } from '@mui/material'
import MuiAccordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { CustomThemeContext } from 'common/providers/CustomThemeProvider'
import { enqueueSnackbar } from 'notistack'
import useFetchFAQ from '../hooks/useFetchFAQ'
import FaqController from '../libs/FaqController'

const Accordion = styled((props) => <MuiAccordion square {...props} />)(({ theme }) => ({
  '& .MuiAccordionSummary-content': {
    flexDirection: 'column',
  },
}))

const VisorPreguntas = () => {
  const { themeController } = useContext(CustomThemeContext)

  const { response, loading } = useFetchFAQ()
  const [preguntas, setPreguntas] = useState(response)

  useEffect(() => setPreguntas(response), [response])

  const [expanded, setExpanded] = useState(false)
  const [pregunta, setPregunta] = useState('')
  const [sending, setSending] = useState(false)

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const handlePregunta = (e) => {
    setPregunta(e.target.value)
  }

  const handleClick = async () => {
    setSending(true)
    const newQuestion = await FaqController.create(pregunta)
    if (newQuestion) {
      enqueueSnackbar('Gracias por su pregunta. Será contestada lo antes posible', {
        variant: 'success',
      })
    } else {
      enqueueSnackbar('Ha ocurrido un error y no se pudo guardar la pregunta', {
        variant: 'error',
      })
    }
    setSending(false)
    setPregunta('')
  }

  return (
    <div>
      <TextField
        sx={{ width: '100%', mb: 2 }}
        id="pregunta"
        placeholder="Escribe tu pregunta"
        variant="outlined"
        value={pregunta}
        onKeyUp={(e) => {
          e.key === 'Enter' && handleClick()
        }}
        onChange={handlePregunta}
        disabled={sending}
      />
      {pregunta !== '' && pregunta.length > 5 ? (
        <Stack>
          <Typography>Si no encuentras respuesta a tu pregunta puedes</Typography>
          <Button variant="contained" sx={{ mb: 2 }} onClick={handleClick} disabled={sending}>
            {sending ? (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <CircularProgress color="terciary" enableTrackSlot size={30} />
                <Typography variant="body1">Enviando pregunta</Typography>
              </Box>
            ) : (
              <Typography variant="body1">Enviar pregunta</Typography>
            )}
          </Button>
        </Stack>
      ) : null}
      {loading ? (
        <Box>
          <Skeleton variant="rounded" width="100%" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width="100%" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width="100%" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width="100%" height={60} sx={{ mb: 1 }} />
        </Box>
      ) : preguntas?.length === 0 ? (
        <Typography variant="body1">No hay preguntas, haz la tuya.</Typography>
      ) : (
        preguntas?.map((p) => {
          return pregunta.split(' ').some((v) => p.texto_pregunta.includes(v)) ? (
            <Accordion
              key={p.id}
              expanded={expanded === p.id}
              onChange={handleChange(p.id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
              >
                <Typography variant="body1">{p.texto_pregunta}</Typography>
                <Typography variant="body2">
                  {!themeController?.hide?.VisorPreguntas?.usuario_pregunta
                    ? `${p.usuario_pregunta} · `
                    : null}
                  {p.fecha_pregunta}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>{p.texto_respuesta}</Typography>
                <Typography>
                  {!themeController?.hide?.VisorPreguntas?.usuario_respuesta
                    ? `${p.usuario_respuesta} · `
                    : null}
                  {p.fecha_respuesta}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ) : null
        })
      )}
    </div>
  )
}

export default VisorPreguntas
